import React, { Component, type ErrorInfo } from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Forward to Sentry if wired up (no direct import needed — Sentry's
    // global error handler captures unhandled errors automatically, but
    // error boundaries require an explicit call).
    const sentry = (window as unknown as Record<string, unknown>).__SENTRY__;
    if (sentry && typeof (sentry as Record<string, unknown>).captureException === 'function') {
      ((sentry as Record<string, unknown>).captureException as (e: unknown, ctx: unknown) => void)(
        error,
        { extra: { componentStack: info.componentStack } },
      );
    }
    // Always log to console so devtools show the full stack.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100dvh',
            padding: '2rem',
            fontFamily: 'system-ui, sans-serif',
            background: 'var(--bg-primary, #0f0f0f)',
            color: 'var(--text-primary, #fff)',
            textAlign: 'center',
            gap: '1rem',
          }}
        >
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>
            Something went wrong
          </h1>
          <p style={{ color: 'var(--text-secondary, #888)', maxWidth: '32ch', margin: 0 }}>
            An unexpected error occurred. Reloading the page usually fixes it.
          </p>
          {this.state.error && (
            <code
              style={{
                fontSize: '0.75rem',
                color: 'var(--text-tertiary, #555)',
                maxWidth: '60ch',
                wordBreak: 'break-word',
              }}
            >
              {this.state.error.message}
            </code>
          )}
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              marginTop: '0.5rem',
              padding: '0.625rem 1.5rem',
              background: 'var(--accent, #7c3aed)',
              color: '#fff',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
